import axios from "axios";
import { isRecentlyActive } from "@/lib/activity";

// Cookie-only auth: httpOnly Secure cookies carry the session. No tokens in
// localStorage. The short-lived pre-auth (2FA step-up) token lives in React
// memory only and is passed explicitly as a Bearer header to the MFA endpoints.
const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Ein Fehler ist aufgetreten. Bitte erneut versuchen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// --- Session resilience ---------------------------------------------------
// On a 401 from a normal API call, transparently refresh the access cookie ONCE
// and retry the original request (so navigation/reload never forces re-login while
// the 8h session is valid). Transient refresh failures must not end the session.
let _refreshPromise = null;
const _isAuthFlow = (url = "") =>
  /\/auth\/(login|refresh|logout|2fa|change-password|password-setup|owner-recovery)/.test(url);

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const { response, config } = error;
    if (!response || !config) return Promise.reject(error); // network/timeout -> no logout
    const is401 = response.status === 401 && !_isAuthFlow(config.url || "");
    if (is401 && !config._retried) {
      config._retried = true;
      try {
        if (!_refreshPromise) {
          _refreshPromise = api.post("/auth/refresh", { active: isRecentlyActive() }).finally(() => { _refreshPromise = null; });
        }
        await _refreshPromise;
      } catch (refreshError) {
        // Only an authentication rejection proves that the session has ended.
        // Offline, timeout, rate-limit and server errors leave it recoverable.
        const refreshStatus = refreshError?.response?.status;
        if (refreshStatus === 401 || refreshStatus === 403) {
          window.dispatchEvent(new CustomEvent("auth:session-ended"));
        }
        return Promise.reject(refreshError);
      }
      // Keep the retry outside the refresh catch: a permission error or network
      // failure on the original resource must not be mistaken for failed auth.
      return api(config);
    }
    if (is401 && config._retried) {
      window.dispatchEvent(new CustomEvent("auth:session-ended"));
    }
    return Promise.reject(error);
  }
);

export default api;
